'use client'

import { useLocale, useTranslations } from 'next-intl'
import { use } from 'react'
import useSWR from 'swr'

import Swimlane, { SwimlaneSkeleton } from '@/app/[locale]/(worlds)/components/Swimlane'
import { SWIMLANE_SCROLL_BLEED_CLASS } from '@/constants/swimlane'
import { useConfigsContext } from '@/context/ConfigsContext'
import { useUAContext } from '@/context/UAContext'
import { filterNonePremiumWorlds } from '@/lib/cms'
import { EditorChoiceCards, getEditorChoices } from '@/services/cms'

type NewAndNoteWorthyProps = {
  newWorldsPromise: Promise<EditorChoiceCards>
}

export default function NewAndNoteWorthy({ newWorldsPromise }: NewAndNoteWorthyProps) {
  const newWorlds = use(newWorldsPromise)
  const locale = useLocale()
  const t = useTranslations()
  const { totalVramKb } = useUAContext()
  const { store } = useConfigsContext()
  const htcToken = store?.credentials?.htcToken

  // 🥷🏼 Use fallbackData to prevent initial re-fetch on mount,
  // 🥷🏼 but still get fresh data when SWR cache is invalidated (e.g., when user likes/unlikes a world)
  const { data = newWorlds } = useSWR(
    htcToken ? 'newWorlds' : null,
    () =>
      getEditorChoices(undefined, undefined, 'new', locale, totalVramKb, false, htcToken).then(
        (res) => {
          const editorChoices = res?.results?.results || []
          return filterNonePremiumWorlds(Promise.resolve(editorChoices))
        },
      ),
    {
      fallbackData: newWorlds,
      revalidateOnMount: false,
    },
  )

  if (!data.rooms || data.rooms.length === 0) {
    return null
  }

  return (
    <Swimlane
      title={t('worlds-landing.new.new-and-noteworthy.title')}
      className="pt-8"
      world={data}
      cardVariant="viverse"
      dataRoomIdPrefix="awc"
      suppressFreshnessBadges
      hideLiked
      scrollClassName={`${SWIMLANE_SCROLL_BLEED_CLASS} gap-2 md:gap-4`}
      scrollContainerClassName="-mx-4 md:-mx-4 lg:-mx-6"
    />
  )
}

export function NewAndNoteWorthySkeleton() {
  return <SwimlaneSkeleton className="pt-8" />
}
